import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea, EstadoVazio } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  diaMes,
  diaLongo,
  diasDoPeriodo,
  gravarCelulas,
  hojeBrasilia,
  inicioDaSemana,
  lerQuadro,
  siglaDoDia,
  somarDias,
} from '../../escala';
import type {
  CelulaDaEscala,
  MudancaDeCelula,
  PessoaDaEscala,
  QuadroDeHorario,
  TurnoDaEscala,
} from '../../escala';
import {
  ROTULO_DA_SITUACAO_DO_CAIXA,
  alertasDoDia,
  diaDoCaixa,
  ehCaixa,
  ehDomingo,
  grupoDoTurno,
  montarRevezamento,
  nomeDoPosto,
  numeroDoCaixa,
} from './caixas';
import type { DiaDoCaixa, SituacaoDoCaixa } from './caixas';
import { resumoDaFolgaFixa } from './folga-fixa';
import {
  blocosDeFerias,
  estiloDoTurno,
  irParaEscala,
  textoDaCelula,
  useEscFecha,
} from './QuadroDeHorario';

/**
 * ABA CAIXAS (dono, 26/10/2026): "revezamento, folga, férias, caixa, horário, almoço — tudo".
 *
 *   Semana dos caixas   dia a dia (segunda a domingo): quem está em cada caixa, horário e almoço;
 *                       ao lado, quem folga, quem está de férias e quem faltou.
 *   Por pessoa          a semana de cada caixa numa linha (posto, horário, almoço ou o motivo).
 *   Resumo              horário da ficha, folga fixa, dias e domingos da semana, próximas férias.
 *   Alertas             almoço junto, dois no mesmo caixa, caixa sem posto.
 *
 * Carlos/Nicolas montam o revezamento num toque (o posto gira e o almoço é escalonado) e
 * acertam qualquer dia à mão; a equipe vê. A folha sai pronta para imprimir (A4 deitada).
 */

const JANELA_DAS_FERIAS = 90;

export function AbaCaixas({
  sessao,
  parametros,
  rota,
}: {
  sessao: Sessao;
  parametros: URLSearchParams;
  rota: string;
}) {
  const semana = inicioDaSemana(parametros.get('de') ?? hojeBrasilia());
  const fim = somarDias(semana, 6);
  const [q, setQ] = useState<QuadroDeHorario | null>(null);
  const [erro, setErro] = useState(false);
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);
  const [gravando, setGravando] = useState(false);
  const [aberto, setAberto] = useState<DiaDoCaixa | null>(null);

  const carregar = useCallback(() => {
    setErro(false);
    // a semana + 90 dias à frente (as próximas férias de cada caixa)
    void lerQuadro(semana, somarDias(semana, JANELA_DAS_FERIAS)).then((r) =>
      r.ok ? setQ(r.dados) : setErro(true),
    );
  }, [semana]);
  useEffect(() => {
    setQ(null);
    carregar();
  }, [carregar]);

  const turnos = useMemo(() => new Map((q?.turnos ?? []).map((t) => [t.codigo, t])), [q]);
  const celulas = useMemo(
    () => new Map((q?.celulas ?? []).map((c) => [`${c.pessoaId}|${c.dia}`, c])),
    [q],
  );
  const dias = useMemo(() => diasDoPeriodo(semana, fim), [semana, fim]);

  if (erro) return <ErroDeArea oQue="a escala dos caixas" aoTentar={carregar} />;
  if (!q) return <div className="qh-carregando">Carregando os caixas…</div>;

  const editor = q.podeEditar;
  const daSemana = q.celulas.filter((c) => c.dia >= semana && c.dia <= fim);
  const caixas = q.pessoas.filter((p) => p.ativo && ehCaixa(p, daSemana));
  const feriados = new Map(q.feriados.map((f) => [f.dia, f.nome]));
  const grade = new Map<string, DiaDoCaixa>();
  for (const p of caixas)
    for (const d of dias)
      grade.set(`${p.id}|${d}`, diaDoCaixa(p, d, celulas.get(`${p.id}|${d}`) ?? null, turnos));
  const doDia = (d: string) => caixas.map((p) => grade.get(`${p.id}|${d}`)).filter((x) => !!x);
  const postos = Math.max(
    1,
    ...[...grade.values()].map((x) => x.posto ?? 0),
    // cada turno usa os seus caixas: conta o turno mais cheio do dia, não a soma
    ...dias.flatMap((d) => {
      const trab = doDia(d).filter((x) => x.situacao === 'TRABALHO');
      return [
        trab.filter((x) => grupoDoTurno(x) === 'MANHA').length,
        trab.filter((x) => grupoDoTurno(x) === 'TARDE').length,
      ];
    }),
  );
  const alertas = dias.flatMap((d) => {
    const trab = doDia(d).filter((x) => x.situacao === 'TRABALHO');
    const semPosto = trab.filter((x) => x.posto === null);
    return [
      ...alertasDoDia(d, trab),
      ...(semPosto.length > 0
        ? [
            {
              dia: d,
              texto: `${semPosto.map((x) => x.pessoa.nome).join(', ')} sem caixa definido.`,
            },
          ]
        : []),
    ];
  });
  const ferias = blocosDeFerias(
    q.celulas
      .filter((c) => turnos.get(c.turno)?.categoria === 'FERIAS')
      .map((c) => ({ pessoaId: c.pessoaId, dia: c.dia })),
    caixas,
  );

  const gravar = async (m: MudancaDeCelula[], texto: string) => {
    if (m.length === 0) return setAviso({ tom: 'ok', texto: 'Nada a mudar — já estava assim.' });
    setGravando(true);
    const r = await gravarCelulas(sessao, m);
    setGravando(false);
    if (r.tipo === 'confirmada') setAviso({ tom: 'ok', texto });
    else
      setAviso({
        tom: 'erro',
        texto:
          r.tipo === 'recusada' ? r.mensagem : 'A internet caiu no meio — confira (recarreguei).',
      });
    carregar();
  };

  const montar = () => {
    const m = montarRevezamento(semana, caixas, celulas, turnos);
    if (m.length === 0)
      return setAviso({ tom: 'ok', texto: 'O revezamento desta semana já está montado.' });
    if (
      !window.confirm(
        `Montar o revezamento de ${diaMes(semana)} a ${diaMes(fim)}?\n\n` +
          `O caixa de cada um gira a cada dia e o almoço fica escalonado (dois caixas nunca almoçam juntos). ` +
          `${String(m.length)} dias de trabalho mudam; folgas, férias, faltas e atestados ficam como estão.`,
      )
    )
      return;
    void gravar(
      m,
      `Revezamento de ${diaMes(semana)} a ${diaMes(fim)} montado (${String(m.length)} dias).`,
    );
  };

  return (
    <div className="qh-corpo qc">
      <div className="qh-barra qh-nao-imprime">
        <div className="qh-periodo">
          <button
            type="button"
            className="qh-btn-icone"
            onClick={() => irParaEscala(rota, { de: somarDias(semana, -7) })}
            aria-label="Semana anterior"
          >
            <Icone nome="arrow-l" tamanho={16} />
          </button>
          <span className="qh-periodo-txt">
            {diaMes(semana)} a {diaMes(fim)}
          </span>
          <button
            type="button"
            className="qh-btn-icone"
            onClick={() => irParaEscala(rota, { de: somarDias(semana, 7) })}
            aria-label="Próxima semana"
          >
            <Icone nome="arrow" tamanho={16} />
          </button>
          {semana !== inicioDaSemana(q.hoje) && (
            <button
              type="button"
              className="qh-btn qh-btn-sec qh-btn-curto"
              onClick={() => irParaEscala(rota, { de: null })}
            >
              Esta semana
            </button>
          )}
        </div>
        <div className="qh-acoes">
          {gravando && <span className="qh-gravando">Salvando…</span>}
          {editor && caixas.length > 0 && (
            <button
              type="button"
              className="qh-btn qh-btn-pri"
              onClick={montar}
              disabled={gravando}
            >
              <Icone nome="cycle" tamanho={15} />
              Montar revezamento da semana
            </button>
          )}
          <button type="button" className="qh-btn qh-btn-sec" onClick={() => window.print()}>
            <Icone nome="down" tamanho={15} />
            Imprimir / PDF
          </button>
        </div>
      </div>

      {aviso && (
        <div
          className={`qh-aviso qh-aviso-${aviso.tom} qh-nao-imprime`}
          role={aviso.tom === 'erro' ? 'alert' : 'status'}
        >
          {aviso.texto}
          <button type="button" onClick={() => setAviso(null)} aria-label="Fechar aviso">
            <Icone nome="x" tamanho={14} />
          </button>
        </div>
      )}

      {caixas.length === 0 ? (
        <EstadoVazio
          icone="users"
          titulo="Ninguém marcado como caixa"
          texto={
            editor
              ? 'No Quadro da loja, toque no nome da pessoa e ponha a função “Caixa” na ficha — ela aparece aqui.'
              : 'O Carlos ainda não marcou quem é caixa.'
          }
        />
      ) : (
        <div className="qc-folha">
          <div className="qc-titulo-impressao">
            <strong>CAIXAS · REVEZAMENTO</strong>
            <span>
              Semana de {diaMes(semana)} a {diaMes(fim)}
            </span>
          </div>

          {alertas.length > 0 && (
            <div className="qc-alertas" role="status">
              <Icone nome="warn" tamanho={16} />
              <ul>
                {alertas.map((a) => (
                  <li key={`${a.dia}${a.texto}`}>
                    <strong>
                      {siglaDoDia(a.dia)} {diaMes(a.dia)}:
                    </strong>{' '}
                    {a.texto}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <section className="qc-bloco">
            <h2>Semana dos caixas</h2>
            <div className="qh-rolagem">
              <table className="qc-tabela">
                <thead>
                  <tr>
                    <th className="qc-dia-col">Dia</th>
                    {Array.from({ length: postos }, (_, i) => (
                      <th key={i}>{nomeDoPosto(i + 1)}</th>
                    ))}
                    <th className="qc-sem-posto">Sem caixa definido</th>
                    <th>Folga</th>
                    <th>Férias</th>
                    <th>Falta / atestado</th>
                  </tr>
                </thead>
                <tbody>
                  {dias.map((d) => {
                    const todos = doDia(d);
                    const trab = todos.filter((x) => x.situacao === 'TRABALHO');
                    const nomes = (s: readonly SituacaoDoCaixa[]) =>
                      todos.filter((x) => s.includes(x.situacao));
                    return (
                      <tr
                        key={d}
                        className={`${ehDomingo(d) ? 'qc-dom' : ''}${d === q.hoje ? ' qc-hoje' : ''}`}
                      >
                        <th className="qc-dia-col" scope="row">
                          <strong>{siglaDoDia(d)}</strong> {diaMes(d)}
                          {feriados.get(d) && (
                            <small className="qc-feriado">{feriados.get(d)}</small>
                          )}
                        </th>
                        {Array.from({ length: postos }, (_, i) => {
                          const nele = trab
                            .filter((x) => x.posto === i + 1)
                            .sort((a, b) => (a.entrada ?? '').localeCompare(b.entrada ?? ''));
                          return (
                            <td key={i} className={nele.length === 0 ? 'qc-vazio' : undefined}>
                              {nele.length === 0 ? (
                                <span className="qc-mudo">—</span>
                              ) : (
                                nele.map((x) => (
                                  <CartaoDoCaixa
                                    key={x.pessoa.id}
                                    x={x}
                                    editor={editor}
                                    aoAbrir={() => setAberto(x)}
                                  />
                                ))
                              )}
                            </td>
                          );
                        })}
                        <td className="qc-sem-posto">
                          {trab
                            .filter((x) => x.posto === null || x.posto > postos)
                            .map((x) => (
                              <CartaoDoCaixa
                                key={x.pessoa.id}
                                x={x}
                                editor={editor}
                                aoAbrir={() => setAberto(x)}
                              />
                            ))}
                        </td>
                        <td>
                          <Nomes lista={nomes(['FOLGA', 'COMPENSADA', 'FERIADO'])} />
                        </td>
                        <td>
                          <Nomes lista={nomes(['FERIAS'])} />
                        </td>
                        <td>
                          <Nomes lista={nomes(['FALTA', 'ATESTADO'])} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="qc-bloco">
            <h2>Por pessoa</h2>
            <div className="qh-rolagem">
              <table className="qc-tabela qc-por-pessoa">
                <thead>
                  <tr>
                    <th className="qc-dia-col">Caixa</th>
                    {dias.map((d) => (
                      <th key={d} className={ehDomingo(d) ? 'qc-dom' : undefined}>
                        {siglaDoDia(d)} {diaMes(d)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {caixas.map((p) => (
                    <tr key={p.id}>
                      <th className="qc-dia-col" scope="row">
                        {p.nome}
                      </th>
                      {dias.map((d) => {
                        const x = grade.get(`${p.id}|${d}`);
                        if (!x) return <td key={d} />;
                        return (
                          <td
                            key={d}
                            className={`${ehDomingo(d) ? 'qc-dom' : ''}${editor && x.situacao === 'TRABALHO' ? ' qc-edita' : ''}`}
                            onClick={() => editor && x.situacao === 'TRABALHO' && setAberto(x)}
                          >
                            <DiaCurto x={x} turno={x.turno} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="qc-bloco">
            <h2>Resumo dos caixas</h2>
            <div className="qh-rolagem">
              <table className="qc-tabela qc-resumo">
                <thead>
                  <tr>
                    <th className="qc-dia-col">Caixa</th>
                    <th>Horário (ficha)</th>
                    <th>Almoço (ficha)</th>
                    <th>Folga fixa</th>
                    <th>Dias na semana</th>
                    <th>Domingos</th>
                    <th>Próximas férias</th>
                  </tr>
                </thead>
                <tbody>
                  {caixas.map((p) => {
                    const sem = dias.map((d) => grade.get(`${p.id}|${d}`)).filter((x) => !!x);
                    const trab = sem.filter((x) => x.situacao === 'TRABALHO');
                    const fe = ferias.filter((f) => f.pessoaId === p.id && f.ate >= semana);
                    return (
                      <tr key={p.id}>
                        <th className="qc-dia-col" scope="row">
                          {p.nome}
                          {p.horario.abreLoja && <small className="qc-tag">abre</small>}
                          {p.horario.fechaLoja && <small className="qc-tag">fecha</small>}
                        </th>
                        <td>{horaDaFicha(p, q.turnos)}</td>
                        <td>
                          {p.horario.almocoInicio && p.horario.almocoFim
                            ? `${p.horario.almocoInicio}–${p.horario.almocoFim}`
                            : '—'}
                        </td>
                        <td className="qc-esq">
                          {p.folgaFixa
                            ? resumoDaFolgaFixa(p.folgaFixa, q.turnos)
                            : 'Sem folga fixa'}
                        </td>
                        <td>{trab.length}</td>
                        <td>{trab.filter((x) => ehDomingo(x.dia)).length}</td>
                        <td className="qc-esq">
                          {fe.length === 0
                            ? `— (até ${diaMes(somarDias(semana, JANELA_DAS_FERIAS))})`
                            : fe
                                .slice(0, 2)
                                .map(
                                  (f) => `${diaMes(f.de)} a ${diaMes(f.ate)} (${String(f.dias)} d)`,
                                )
                                .join(' · ')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <p className="qc-rodape-impressao">
            Almoço com * = acertado no dia (revezamento); sem * = o da ficha/turno. Gerado no
            InovaPay em{' '}
            {new Date().toLocaleString('pt-BR', {
              timeZone: 'America/Sao_Paulo',
              dateStyle: 'short',
              timeStyle: 'short',
            })}
            .
          </p>
        </div>
      )}

      {aberto && aberto.celula && (
        <EditorDoCaixa
          x={aberto}
          celula={aberto.celula}
          postos={Math.max(postos, 3)}
          aoFechar={() => setAberto(null)}
          aoGravar={(m, texto) => {
            setAberto(null);
            void gravar([m], texto);
          }}
        />
      )}
    </div>
  );
}

function horaDaFicha(p: PessoaDaEscala, turnos: readonly TurnoDaEscala[]): string {
  if (p.horario.entrada || p.horario.saida)
    return `${p.horario.entrada ?? '?'}–${p.horario.saida ?? '?'}`;
  const t = turnos.find((x) => x.codigo === p.turnoPadrao);
  return t?.entrada || t?.saida ? `${t.entrada ?? '?'}–${t.saida ?? '?'} (${t.rotulo})` : '—';
}

function CartaoDoCaixa({
  x,
  editor,
  aoAbrir,
}: {
  x: DiaDoCaixa;
  editor: boolean;
  aoAbrir: () => void;
}) {
  const corpo = (
    <>
      <strong>{x.pessoa.nome}</strong>
      <span>
        {x.entrada ?? '?'}–{x.saida ?? '?'}
      </span>
      <span className="qc-almoco">
        Almoço {x.almocoInicio && x.almocoFim ? `${x.almocoInicio}–${x.almocoFim}` : '—'}
        {x.almocoDoDia && '*'}
      </span>
    </>
  );
  return editor ? (
    <button
      type="button"
      className="qc-cartao"
      onClick={aoAbrir}
      style={estiloDoTurno(x.turno ?? undefined)}
      title={`${x.pessoa.nome}, ${diaLongo(x.dia)} — trocar caixa ou almoço`}
    >
      {corpo}
    </button>
  ) : (
    <div className="qc-cartao" style={estiloDoTurno(x.turno ?? undefined)}>
      {corpo}
    </div>
  );
}

function Nomes({ lista }: { lista: readonly DiaDoCaixa[] }) {
  if (lista.length === 0) return <span className="qc-mudo">—</span>;
  return (
    <ul className="qc-nomes">
      {lista.map((x) => (
        <li key={x.pessoa.id}>
          {x.pessoa.nome}
          {x.situacao !== 'FOLGA' && x.situacao !== 'FERIAS' && (
            <small> · {ROTULO_DA_SITUACAO_DO_CAIXA[x.situacao]}</small>
          )}
          {x.celula?.compensa && <small> (feriado {diaMes(x.celula.compensa)})</small>}
        </li>
      ))}
    </ul>
  );
}

function DiaCurto({ x, turno }: { x: DiaDoCaixa; turno: TurnoDaEscala | null }) {
  if (x.situacao !== 'TRABALHO')
    return (
      <span className="qc-motivo" style={x.celula ? estiloDoTurno(turno ?? undefined) : undefined}>
        {x.celula
          ? textoDaCelula(x.celula, turno ?? undefined) || ROTULO_DA_SITUACAO_DO_CAIXA[x.situacao]
          : '—'}
        <small>{ROTULO_DA_SITUACAO_DO_CAIXA[x.situacao]}</small>
      </span>
    );
  return (
    <span className="qc-curto">
      <strong>{x.posto ? `C${String(x.posto)}` : '—'}</strong>
      <span>
        {x.entrada ?? '?'}–{x.saida ?? '?'}
      </span>
      <small>
        🍽 {x.almocoInicio ?? '—'}
        {x.almocoDoDia && '*'}
      </small>
    </span>
  );
}

function EditorDoCaixa({
  x,
  celula,
  postos,
  aoFechar,
  aoGravar,
}: {
  x: DiaDoCaixa;
  celula: CelulaDaEscala;
  postos: number;
  aoFechar: () => void;
  aoGravar: (m: MudancaDeCelula, texto: string) => void;
}) {
  const [posto, setPosto] = useState<number | null>(numeroDoCaixa(celula.setor));
  const [doDia, setDoDia] = useState(!!(celula.almocoInicio && celula.almocoFim));
  const [ini, setIni] = useState(celula.almocoInicio ?? x.almocoInicio ?? '12:00');
  const [fimAlm, setFimAlm] = useState(celula.almocoFim ?? x.almocoFim ?? '13:00');
  useEscFecha(aoFechar);
  const invalido = doDia && (!ini || !fimAlm || fimAlm <= ini);
  const outroSetor = celula.setor && numeroDoCaixa(celula.setor) === null ? celula.setor : null;

  const salvar = () => {
    if (invalido) return;
    aoGravar(
      {
        pessoaId: x.pessoa.id,
        dia: x.dia,
        turno: celula.turno,
        setor: posto ? nomeDoPosto(posto) : outroSetor,
        almocoInicio: doDia ? ini : null,
        almocoFim: doDia ? fimAlm : null,
      },
      `${x.pessoa.nome}, ${diaMes(x.dia)}: ${posto ? nomeDoPosto(posto) : 'sem caixa'}, almoço ${doDia ? `${ini}–${fimAlm}` : 'da ficha'}.`,
    );
  };

  return (
    <div className="qh-modal-fundo" onClick={aoFechar} role="presentation">
      <div
        className="qh-modal qh-modal-estreito"
        role="dialog"
        aria-modal="true"
        aria-label={`${x.pessoa.nome}, ${diaLongo(x.dia)}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <div>
            <h2>{x.pessoa.nome}</h2>
            <p>
              {diaLongo(x.dia)} · {x.entrada ?? '?'}–{x.saida ?? '?'}
            </p>
          </div>
          <button type="button" className="qh-btn-icone" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} />
          </button>
        </header>
        <fieldset className="qc-postos">
          <legend>Caixa do dia</legend>
          {Array.from({ length: postos }, (_, i) => (
            <button
              key={i}
              type="button"
              className={`qh-pincel${posto === i + 1 ? ' qh-pincel-on' : ''}`}
              onClick={() => setPosto(i + 1)}
              aria-pressed={posto === i + 1}
            >
              {nomeDoPosto(i + 1)}
            </button>
          ))}
          <button
            type="button"
            className={`qh-pincel${posto === null ? ' qh-pincel-on' : ''}`}
            onClick={() => setPosto(null)}
            aria-pressed={posto === null}
          >
            {outroSetor ? `Fica em ${outroSetor}` : 'Sem caixa'}
          </button>
        </fieldset>
        <label className="qh-check">
          <input type="checkbox" checked={doDia} onChange={(e) => setDoDia(e.target.checked)} />
          <span>
            <strong>Almoço diferente neste dia</strong>
            <small>Desmarcado = vale o almoço da ficha (ou do turno).</small>
          </span>
        </label>
        {doDia && (
          <div className="qc-almoco-campos">
            <label className="qh-campo">
              Sai para almoçar
              <input type="time" value={ini} onChange={(e) => setIni(e.target.value)} />
            </label>
            <label className="qh-campo">
              Volta
              <input type="time" value={fimAlm} onChange={(e) => setFimAlm(e.target.value)} />
            </label>
          </div>
        )}
        {invalido && <p className="qh-aviso qh-aviso-erro">A volta tem de ser depois da saída.</p>}
        <footer>
          <button type="button" className="qh-btn qh-btn-sec" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="qh-btn qh-btn-pri" onClick={salvar} disabled={invalido}>
            Salvar
          </button>
        </footer>
      </div>
    </div>
  );
}
